import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Grid, Typography, Box, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import tw from "twin.macro";

// Japanese-inspired container with appropriate spacing
const Container = tw.div`relative mx-auto px-0 py-0 max-w-screen-xl`;

// Clean container with full outline border
const MessageContainer = styled(Paper)(({ theme }) => ({
  width: "100%",
  position: "relative",
  margin: "0 auto",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
  borderRadius: 4,
  overflow: "hidden",
  display: "flex",
  flexDirection: "row",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const MessageContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  display: "flex",
  flexDirection: "column",
  position: "relative",
  [theme.breakpoints.down("lg")]: {
    padding: theme.spacing(2.5, 3.5),
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2.5),
  },
}));

const ImageColumn = styled(Box)(({ theme }) => ({
  position: "relative",
  borderLeft: "1px solid rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    borderLeft: "none",
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
  },
}));

const DateIndicator = styled(Typography)(({ theme }) => ({
  fontFamily: "'Noto Sans JP', sans-serif",
  fontSize: "0.8rem",
  color: "#666",
  marginBottom: theme.spacing(2),
  display: "inline-flex",
  alignItems: "center",
  position: "relative",
  paddingLeft: theme.spacing(2.5),
  letterSpacing: "0.03em",
  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translateY(-50%)",
    width: 12,
    height: 1,
    backgroundColor: "#0c4d96",
  },
}));

const DirectorNameContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  borderTop: "1px solid rgba(0, 0, 0, 0.08)",
}));

const MessageHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "'Noto Sans JP', sans-serif",
  fontWeight: 500,
  color: "#333",
  marginBottom: theme.spacing(2),
  letterSpacing: "0.02em",
  position: "relative",
  paddingBottom: theme.spacing(0.75),
  "&:after": {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: 0,
    width: 40,
    height: 2,
    backgroundColor: "#0c4d96",
  },
}));

// Main component
const DirectorMessage = ({ imageSrc }) => {
  const { t } = useTranslation();

  // Simple animation
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <Container>
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeIn}
      >
        <MessageContainer elevation={0}>
          <Grid container>
            {/* Left side - Message content */}
            <Grid item xs={12} md={8} display="flex" alignItems="center">
              <MessageContent>
                <DateIndicator>{t("director_message.date")}</DateIndicator>

                <MessageHeading variant="h5">
                  {t("director_message.heading")}
                </MessageHeading>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontSize: "0.9rem",
                    lineHeight: 1.7,
                    color: "#333",
                    letterSpacing: "0.01em",
                  }}
                >
                  {t("director_message.content", { returnObjects: true }).map(
                    (paragraph, index) => (
                      <React.Fragment key={index}>
                        {paragraph}
                        <br />
                        <br />
                      </React.Fragment>
                    )
                  )}
                </Typography>
              </MessageContent>
            </Grid>

            {/* Right side - Image and info */}
            <Grid item xs={12} md={4}>
              <ImageColumn>
                <Box
                  sx={{
                    height: { xs: 280, sm: 320, md: "70%" },
                    position: "relative",
                  }}
                >
                  <img
                    src={imageSrc}
                    alt={t("director_message.name")}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center 20%",
                    }}
                  />
                </Box>

                <DirectorNameContainer>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "'Noto Sans JP', sans-serif",
                          fontSize: "1rem",
                          fontWeight: 500,
                          color: "#333",
                          textAlign: "right",
                          mb: 0.5,
                        }}
                      >
                        {t("director_message.name")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "'Noto Sans JP', sans-serif",
                          fontSize: "0.75rem",
                          color: "#666",
                          textAlign: "right",
                        }}
                      >
                        {t("director_message.title")}
                      </Typography>
                    </Box>
                  </Box>
                </DirectorNameContainer>
              </ImageColumn>
            </Grid>
          </Grid>
        </MessageContainer>
      </motion.div>
    </Container>
  );
};

export default DirectorMessage;
